import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { ExternalSettings } from 'src/app/common/services/external-settings/external-settings.interface';
import { MEDelimiter } from './string-utility.service';

@Injectable({
  providedIn: 'root'
})
export class DateTimeService {

  private static translate: TranslateService;
  public static dateMask: MEDateMask;
  public static timeMask: METimeMask;
  private static months: string[] = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  static init(translate: TranslateService, es: ExternalSettings | undefined = undefined) {
    this.translate = translate;
    if (es) {
      this.dateMask = es.globalVariables.dateMask as MEDateMask;
      this.timeMask = es.globalVariables.timeMask as METimeMask;
    }
  }

  static getDateMaskLocale() {
    return this.dateMask?.toUpperCase() == 'DD/MM/YYYY' ? 'fr-FR' : 'en-US';
  }

  static getCurrentTimstampString() : string {
    return DateTime.now().toFormat('yyyyMMddHHmmss');
  }

  static getCurrentTimstampNumber() : number {
    return Number(DateTimeService.getCurrentTimstampString());
  }

  static getFileNameAtTimestamp(timestamp : Date = new Date()): string {
    let date = (timestamp.getMonth()+1) + '' + timestamp.getDate() + '' + timestamp.getFullYear() + '-';
    let time = timestamp.getHours() + '' + timestamp.getMinutes() + '' + timestamp.getSeconds() + '.';
    let milli = timestamp.getMilliseconds() + '';
    return (date + time + milli);
  }

  static getCurrentTimestampUniqueString() : string {
    return DateTimeService.getCurrentTimstampString() + Math.floor(Math.random() * 1000).toString();
  }

  static getCurrentTimestampUniqueNumber() : number {
    return Number(DateTimeService.getCurrentTimstampString() + Math.floor(Math.random() * 1000).toString());
  }

  static extractddMmmyyyy(dateString: string): DateddMmmyyyy {
    let dateObj = new Date(dateString);
    let date = dateObj.getDate();
    let months = DateTimeService.months.map(m=>(
      this.translate.instant(m)
    ))
    let month = months[dateObj.getMonth()];
    let year = dateObj.getFullYear();
    return {date_dd: date, month_Mmm: month, year_yyyy: year};
  }

  static getdd_Mmm_yyyy(dateString: string, delimiter: MEDelimiter = MEDelimiter.Space): string {
    let date = DateTimeService.extractddMmmyyyy(dateString);
    return date.date_dd + delimiter + date.month_Mmm + delimiter + date.year_yyyy;
  }

  static parseDateTimeDateTimeMasksByInfer(dateTimeStr: string): DateTime | null {
    if (dateTimeStr.length == 0) return null;

    const [datePart, timePart] = dateTimeStr.split(MEDelimiter.Space);

    if (!datePart || !timePart) return null;

    const detectedDateMask = this.detectDateMask(datePart);
    const detectedTimeMask = this.detectTimeMask(timePart);

    if (!detectedDateMask || !detectedTimeMask) {
      // console.error(`Unknown format: ${dateTimeStr}`);
      return null;
    }
    const formatMask = `${detectedDateMask} ${detectedTimeMask}`;
    const parsedDateTime = DateTime.fromFormat(dateTimeStr, formatMask); 
    return parsedDateTime.isValid ? parsedDateTime : null;
  }

  static parseDateTimeWithDateTimeMasks(dateTimeStr: string, dateMask: MEDateMask, timeMask: METimeMask): DateTime | null {
    if (dateTimeStr.length == 0) return null;
    const formatMask = `${dateMask} ${timeMask}`;
    const parsedDateTime = DateTime.fromFormat(dateTimeStr, formatMask); 
    return parsedDateTime.isValid ? parsedDateTime : null;
  }

  static parseDateWithDateTimeMasks(dateStr: string): DateTime | null {
    if (dateStr.length == 0) return null;
    const parsedDate = DateTime.fromFormat(dateStr, this.dateMask); 
    return parsedDate.isValid ? parsedDate : null;
  }

  // Generally a client should have sticked to a certain DateMask type across the system
  // in case some encrypted data in history that cannot be converted based on the system date/time masks
  // detect the datetime string and check the date mask in different cases
  // theoretically there will be some mismatch (should be rare) but better than no match
  static detectDateMask(dateStr: string): MEDateMask | null {
    if (datePatterns.get(MEDateMask.ddMMMyyyy_DASH)?.test(dateStr)) {
      return MEDateMask.ddMMMyyyy_DASH; // Matches "dd-MMM-yyyy"
    }
  
    if (datePatterns.get(MEDateMask.ddMMyyyy_SLASH)?.test(dateStr)) {
      const [first, second] = dateStr.split("/").map(Number);
  
      if (first > 12) {
        return MEDateMask.ddMMyyyy_SLASH; // Must be dd/MM/yyyy (e.g., 27/02/2024)
      } 
      if (second > 12) {
        return MEDateMask.MMddyyyy_SLASH; // Must be MM/dd/yyyy (e.g., 02/27/2024)
      }
      if (this.dateMask !== MEDateMask.ddMMMyyyy_DASH) return this.dateMask; // Default assumption only if not ddMMMyyyy
      else return MEDateMask.ddMMyyyy_SLASH; // choose the most common case to return a type so the sort function can work to some extent
    }
    return null;
  }
  
  static detectTimeMask(timeStr: string): METimeMask | null {
    for (const [mask, pattern] of timePatterns) {
      if (pattern.test(timeStr)) return mask as METimeMask;
    }
    return null;
  }

   /**
   * Returns the start of the current day as a formatted string.
   * @param format The desired format for the date and time string. Defaults to 'MM/dd/yyyy HH:mm:ss'.
   * @returns The start of the current day as a formatted string.
   */
   static getStartOfCurrentDay(format: string = 'MM/dd/yyyy HH:mm:ss'): string {
    const now = DateTime.now();
    const startOfDay = now.startOf('day');
    return startOfDay.toFormat(format);
  }

  /**
   * Returns the end of the current day as a formatted string.
   * @param format The desired format for the date and time string. Defaults to 'MM/dd/yyyy HH:mm:ss'.
   * @returns The end of the current day as a formatted string.
   */
  static getEndOfCurrentDay(format: string = 'MM/dd/yyyy HH:mm:ss'): string {
    const now = DateTime.now();
    const endOfDay = now.endOf('day');
    return endOfDay.toFormat(format);
  }

  /**
   * Converts a time string from 12-hour format (e.g., "9:00 AM") to 24-hour format (e.g., "09:00").
   *
   * @param time12h - The time string in 12-hour format.
   * @returns The time string in 24-hour format.
   */
  static convertTimeTo24HourFormat(time12h: string): string {
    const dt = DateTime.fromFormat(time12h, 'h:mm a');
    return dt.toFormat('HH:mm');
  }

  /**
   * Convert an absolute date string into JS Date Object, which can be used to patch form value
   * @param dateStr : 2025-12-31
   * @returns 2025-12-31 00:00:00 Date Object
   */
  static convertDateStringToDate(dateStr: string, dateFmt: string = 'yyyy-MM-dd'): Date {
      const dt = DateTime.fromFormat(dateStr + ' 00:00:00', `${dateFmt} HH:mm:ss`);
      const jsDate = dt.toJSDate();         // To get a native JS Date object:
      return jsDate;
  }

}

export interface DateddMmmyyyy {
  date_dd: number;
  month_Mmm: string;
  year_yyyy: number;
}

export enum MEDateMask {
  ddMMyyyy_SLASH = 'dd/MM/yyyy', // DAY/MONTH/YEAR
  MMddyyyy_SLASH = 'MM/dd/yyyy', // MONTH/DAY/YEAR
  ddMMMyyyy_DASH = 'dd-MMM-yyyy'
}

export enum METimeMask {
  HHmmss_24 = 'HH:mm:ss', //24 hours with seconds
  HHmm_24 = 'HH:mm', //24 hours no seconds
  HHmmssa_AMPM = 'hh:mm:ssa', //12 hours with seconds
  HHmma_AMPM = 'hh:mma' //12 hours no seconds
}

// Regular expressions to detect date formats
export const datePatterns = new Map<MEDateMask, RegExp>([
  [MEDateMask.ddMMyyyy_SLASH, /^\d{2}\/\d{2}\/\d{4}/],
  // [MEDateMask.MMddyyyy_SLASH, /^\d{2}\/\d{2}\/\d{4}/], Duplicated pattern but keep comment as it is a mask type in system
  // a possible issue: pattern cannot distinguish ddMM and MMdd. Need to input the system dateMask
  [MEDateMask.ddMMMyyyy_DASH, /^\d{2}-[A-Za-z]{3}-\d{4}/]
])

export const timePatterns = new Map<METimeMask, RegExp>([
  [METimeMask.HHmmss_24, /\b\d{2}:\d{2}:\d{2}\b/],
  [METimeMask.HHmm_24, /\b\d{2}:\d{2}\b/],
  [METimeMask.HHmmssa_AMPM, /\b\d{2}:\d{2}:\d{2}(AM|PM|am|pm)\b/],
  [METimeMask.HHmma_AMPM, /\b\d{2}:\d{2}(AM|PM|am|pm)\b/]
])

export const DAY_MAP: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7
};